import { useRef } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { cn } from "@/lib/utils";
import { arrowKeyToDirection, findNavTarget } from "./nav-helpers";

interface NavActionCellProps {
  /** Kolonnenavn for vertikal piltast-navigasjon (samme `data-col` mappes til samme kolonne). */
  dataCol: string;
  /** Callback når brukeren trykker Enter, Space eller klikker på cellen. */
  onActivate: () => void;
  /** Hvis true, ignoreres aktivering og piltaster (cellen er bare et plassholder). */
  disabled?: boolean;
  className?: string;
  title?: string;
  /** `colSpan` for f.eks. "Ny timeføring…"-raden som strekker seg over hele tabellen. */
  colSpan?: number;
  children: ReactNode;
}

/**
 * Navigerbar action-celle i admin-timer-gridet. Brukes for "+"-knapper,
 * "Slett ×" og "Ny timeføring…"-raden. Får piltast-navigasjon på lik linje
 * med EditableCell og aktiveres med Enter eller Space.
 */
export function NavActionCell({
  dataCol,
  onActivate,
  disabled,
  className,
  title,
  colSpan,
  children,
}: NavActionCellProps) {
  const tdRef = useRef<HTMLTableCellElement>(null);

  const handleKey = (e: KeyboardEvent<HTMLTableCellElement>) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onActivate();
      return;
    }
    const dir = arrowKeyToDirection(e);
    if (dir) {
      e.preventDefault();
      const target = findNavTarget(tdRef.current, dir);
      target?.focus();
    }
  };

  return (
    <td
      ref={tdRef}
      data-nav-cell={!disabled ? "true" : undefined}
      data-col={dataCol}
      tabIndex={disabled ? undefined : 0}
      colSpan={colSpan}
      title={title}
      className={cn("focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset", className)}
      onClick={() => !disabled && onActivate()}
      onKeyDown={handleKey}
    >
      {children}
    </td>
  );
}
